if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);
        const operations = window.AugCartOperations;
        const operation = operations?.begin();
        let attempt = this.pendingAddAttempt || { formData, variantId, quantity, sent: false };
        try {
          let response;
          if (attempt.sent) {
            response = await this.recoverAddAttempt(attempt);
          } else {
            // Read before sending so a lost response can be reconciled without a second POST.
            attempt.before = await operations.fetchCart();
            attempt.sent = true;
            this.pendingAddAttempt = attempt;
            response = await operations.fetchJSON(`${routes.cart_add_url}`, config);
          }
          if (response.status) {
            this.pendingAddAttempt = null;
          } else {
            attempt.confirmed = true;
            this.pendingAddAttempt = null;
          }
          this.completeAddResponse(response, linesUpdateDeferred, variantId, evt);
        } catch (error) {
          let recovered = false;
          if (attempt.sent) {
            try {
              const response = await this.recoverAddAttempt(attempt);
              attempt.confirmed = true;
              this.pendingAddAttempt = null;
              this.completeAddResponse(response, linesUpdateDeferred, attempt.variantId, evt);
              recovered = true;
            } catch (_recoveryError) {
              this.pendingAddAttempt = attempt;
              this.showCartRecovery(
                attempt.confirmed ? window.cartStrings.addRecovered : window.cartStrings.addUncertain
              );
            }
          } else {
            this.handleErrorMessage(window.cartStrings.addError);
          }
          if (!recovered) {
            this.dispatchCartErrorEvent(error.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(error);
          }
        } finally {
          operations?.end(operation);
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
          CartPerformance.measureFromEvent('add:user-action', evt);
        }
      }

      matchingCartLine(cart, attempt) {
        const properties = Object.fromEntries([...attempt.formData.entries()]
          .filter(([name, value]) => name.startsWith('properties[') && value !== '')
          .map(([name, value]) => [name.slice(11, -1), String(value)]));
        const plan = String(attempt.formData.get('selling_plan') || '');
        return cart.items.find((item) => String(item.variant_id || item.id) === String(attempt.variantId)
          && String(item.selling_plan_allocation?.selling_plan?.id || '') === plan
          && Object.keys({ ...properties, ...(item.properties || {}) }).every(
            (key) => String(item.properties?.[key] || '') === String(properties[key] || '')
          ));
      }

      async recoverAddAttempt(attempt) {
        const operations = window.AugCartOperations;
        const current = await operations.fetchCart();
        const previous = this.matchingCartLine(attempt.before, attempt)?.quantity || 0;
        const line = this.matchingCartLine(current, attempt);
        if (!line || line.quantity < previous + attempt.quantity) throw new Error('ADD_UNCONFIRMED');
        attempt.confirmed = true;
        if (!this.cart) return { ...line };
        const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
        const url = new URL(window.location.pathname, window.location.origin);
        url.searchParams.set('sections', sectionIds.join(','));
        const sections = await operations.fetchJSON(url.toString(), { cache: 'no-store' });
        if (sectionIds.some((id) => !sections[id])) throw new Error('RECOVERY_SECTIONS_UNAVAILABLE');
        return { ...line, sections };
      }

      showCartRecovery(message) {
        this.handleErrorMessage(message);
        if (!this.errorMessage) return;
        const controls = document.createElement('span');
        controls.className = 'aug-cart-recovery';
        controls.style.cssText = 'display:flex;flex-wrap:wrap;gap:12px;margin-top:8px';
        const cartLink = document.createElement('a');
        cartLink.href = window.routes.cart_url;
        cartLink.className = 'link underlined-link';
        cartLink.textContent = window.cartStrings.checkCart;
        const check = document.createElement('button');
        check.type = 'button';
        check.className = 'link underlined-link';
        check.textContent = window.cartStrings.retryCheck;
        check.addEventListener('click', () => this.form.requestSubmit(this.submitButton));
        controls.append(cartLink, check);
        this.errorMessage.append(controls);
      }

      completeAddResponse(response, linesUpdateDeferred, variantId, evt) {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
